import type { HatchProperties } from "geom/hatchTypes";
import type { AABB3 } from "./aabb3Types";
import type { Vec3 } from "./vec3";

export const BSP_EPSILON = 1e-3;
export const BSP_EPSILON_SQUARED = BSP_EPSILON * BSP_EPSILON;
export const REMOVE_COPLANAR_LINES_EPSILON = 0.01;
export const PROP_BSP_EPSILON = 1e-5;

export enum TraversalOrderBSP {
  FRONT_TO_BACK,
  BACK_TO_FRONT,
}

export enum TraversalCullingBSP {
  NONE,
  BACK,
  FRONT,
}

export interface PlaneBSP {
  nX: number;
  nY: number;
  nZ: number;
  dist: number;
}

export interface LineBSP {
  x1: number;
  y1: number;
  z1: number;
  x2: number;
  y2: number;
  z2: number;
}

export interface ExtendedDataPolygonBSP {
  id: number;
  visibleLines: LineBSP[];
  splitLines: LineBSP[];
  hatchLines: LineBSP[];
  neighborNormals: Vec3[];
  edgePlanes: PlaneBSP[];
  aabb: AABB3;
  hatchProperties?: HatchProperties;
  entityId?: string;
  propId?: string; // Unique id generated for prop geometry at BSP creation time.
}

export interface PolygonBSP {
  priority: number;
  vertices: number[]; // In 3D space
  plane: PlaneBSP;
  extendedData?: ExtendedDataPolygonBSP;
}

export interface LineSplitBSP {
  frontPolygon: PolygonBSP;
  line: LineBSP;
}

export interface PolygonSplitBSP {
  frontPolygon: PolygonBSP;
  backPolygon: PolygonBSP;
  splitLine: LineBSP | null;
}

export interface NodeBSP {
  plane: PlaneBSP;
  polygons: PolygonBSP[];
  parentNode: NodeBSP | null;
  frontNode: NodeBSP | null;
  backNode: NodeBSP | null;
  userData?: any;
}

export interface RenderInfoBSP {
  isPerspectiveMatrix: boolean;
  viewX: number;
  viewY: number;
  viewZ: number;
  viewPosX: number;
  viewPosY: number;
  viewPosZ: number;
}

export interface IntersectionPointBSP {
  x: number;
  y: number;
  z: number;
  t: number;
  userData?: any;
}

export interface RayIntersectionPointBSP extends IntersectionPointBSP {
  distToStart: number;
  nX: number;
  nY: number;
  nZ: number;
}

export interface RayIntersectionBSP {
  entryPoint?: RayIntersectionPointBSP;
  exitPoint?: RayIntersectionPointBSP;
}

export type MaxBSPDepthErrorHandler = () => void;
